import { jsx as _jsx } from "react/jsx-runtime";
/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useReducer, useContext, useCallback, useMemo, useState, } from 'react';
import { ConnectionState } from './types';
var ActionType;
(function (ActionType) {
    ActionType["REGISTER_CHAINS"] = "REGISTER_CHAINS";
    ActionType["SET_SELECTED_CHAIN"] = "SET_SELECTED_CHAIN";
    ActionType["SET_WALLET_CONNECTION"] = "SET_WALLET_CONNECTION";
})(ActionType || (ActionType = {}));
const MultichainModalContext = createContext({
    chains: [],
    isWalletModalOpen: false,
    selectedChain: null,
    walletConnections: {},
    setIsWalletModalOpen: () => { },
    registerChains: () => { },
    setSelectedChain: () => { },
    setWalletConnection: () => { },
    getConnectionState: () => ConnectionState.Disconnected,
    _forceUpdate: 0,
});
function reducer(state, action) {
    switch (action.type) {
        case ActionType.REGISTER_CHAINS: {
            const newChains = action.payload.filter((newChain) => !state.chains.some((prevChain) => prevChain.id === newChain.id));
            return {
                ...state,
                chains: [...state.chains, ...newChains],
            };
        }
        case ActionType.SET_SELECTED_CHAIN:
            return {
                ...state,
                selectedChain: action.payload,
                _forceUpdate: Date.now(),
            };
        case ActionType.SET_WALLET_CONNECTION:
            return {
                ...state,
                walletConnections: {
                    ...state.walletConnections,
                    [action.payload.chainType]: action.payload.state,
                },
                selectedChain: action.payload.chainType === state.selectedChain?.type &&
                    action.payload.state === ConnectionState.Disconnected
                    ? null
                    : state.selectedChain,
            };
        default:
            return state;
    }
}
export const MultichainWalletSwitcher = ({ children }) => {
    const [state, dispatch] = useReducer(reducer, {
        chains: [],
        selectedChain: null,
        walletConnections: {},
        _forceUpdate: 0,
    });
    const registerChains = useCallback((newChains) => {
        dispatch({ type: ActionType.REGISTER_CHAINS, payload: newChains });
    }, []);
    const setSelectedChain = useCallback((chain) => {
        dispatch({ type: ActionType.SET_SELECTED_CHAIN, payload: chain });
    }, []);
    const setWalletConnection = useCallback((chainType, connectionState) => {
        dispatch({
            type: ActionType.SET_WALLET_CONNECTION,
            payload: { chainType, state: connectionState },
        });
    }, []);
    const getConnectionState = useCallback(() => {
        const connectionStates = Object.values(state.walletConnections);
        if (connectionStates.includes(ConnectionState.Connected))
            return ConnectionState.Connected;
        if (connectionStates.includes(ConnectionState.Connecting))
            return ConnectionState.Connecting;
        return ConnectionState.Disconnected;
    }, [state.walletConnections]);
    const [isWalletModalOpen, setIsWalletModalOpen] = useState(false);
    const value = useMemo(() => ({
        ...state,
        isWalletModalOpen,
        setIsWalletModalOpen,
        registerChains,
        setSelectedChain,
        setWalletConnection,
        getConnectionState,
    }), [
        state,
        isWalletModalOpen,
        registerChains,
        setSelectedChain,
        setWalletConnection,
        getConnectionState,
    ]);
    return (_jsx(MultichainModalContext.Provider, { value: value, children: children }));
};
export const useMultichainWalletSwitcher = () => {
    const context = useContext(MultichainModalContext);
    if (!context) {
        throw new Error('useMultichainWalletSwitcher must be used within MultichainWalletSwitcher');
    }
    return context;
};
